/**
 * Label / tone / next-step / settings-range lookups for the Codex
 * capacity-alert operator surface (#2575).
 *
 * Pure functions in their own module so the switch's three-way reading
 * and the client-side range checks can be asserted directly, without
 * mounting the page.
 */
import type {
  CodexCapacityAlertSettings,
  CodexCapacityAlertStateResponse,
  CodexCapacitySettingsIssueCode,
  UpdateCodexCapacityAlertSettingsRequest,
} from '@nadoutong/contracts';
import { validateCodexCapacityAlertSettings } from '@nadoutong/contracts';
import type { I18nKey } from '../../i18n/index.ts';
import type { ChipTone } from '../../ui/index.ts';

/**
 * What the switch is saying, as three states rather than two.
 *
 * `enabled === false` is the ORDINARY state of a freshly promoted
 * environment: the settings row ships closed precisely so a promote
 * cannot produce a first mail until somebody opens it. So "off" alone
 * is not a message — the useful question is whether anybody has ever
 * decided anything, and `updated_at` is the only field that answers it.
 *
 * Collapsing `never_opened` and `closed` into one "disabled" state is
 * the mistake this type exists to prevent: they read identically on a
 * lamp and mean opposite things to whoever finds the page.
 */
export type CodexCapacitySwitchState = 'open' | 'closed' | 'never_opened';

export function readSwitchState(settings: CodexCapacityAlertSettings): CodexCapacitySwitchState {
  if (settings.enabled) return 'open';
  return settings.updated_at === null ? 'never_opened' : 'closed';
}

const SWITCH_STATE_TONE: Record<CodexCapacitySwitchState, ChipTone> = {
  open: 'ok',
  closed: 'bad',
  never_opened: 'warn',
};

const SWITCH_STATE_LABEL: Record<CodexCapacitySwitchState, I18nKey> = {
  open: 'codex_capacity.switch.state_open',
  closed: 'codex_capacity.switch.state_closed',
  never_opened: 'codex_capacity.switch.state_never_opened',
};

export function switchStateTone(state: CodexCapacitySwitchState): ChipTone {
  return SWITCH_STATE_TONE[state];
}

export function switchStateLabel(state: CodexCapacitySwitchState): I18nKey {
  return SWITCH_STATE_LABEL[state];
}

export type CodexCapacityNextStepKind =
  | 'disabled'
  | 'archive_stale'
  | 'quiet_hours'
  | 'cooldown'
  | 'fire'
  | 'recover'
  | 'catch_up'
  | 'firing_remaining'
  | 'firing_no_stamp'
  | 'undersampled'
  | 'hold';

export interface CodexCapacityNextStep {
  readonly kind: CodexCapacityNextStepKind;
  readonly firePct: number;
  readonly clearPct: number;
  readonly remainingMinutes: number;
  readonly clearWindowMinutes: number;
  readonly requests: number;
  readonly minRequests: number;
  readonly owedCount: number;
}

/**
 * One sentence answering "what happens next", not a dump of field names.
 *
 * Quiet hours and cooldown beat a fire/recover verdict because those
 * verdicts do not send and do not latch while the window is closed.
 * Remaining-minutes only applies while firing with a stamp: that is
 * the duration the recover rule actually waits on.
 */
export function describeNextStep(
  settings: CodexCapacityAlertSettings,
  state: CodexCapacityAlertStateResponse,
  now: Date = new Date(),
): CodexCapacityNextStep {
  const base: CodexCapacityNextStep = {
    kind: 'hold',
    firePct: settings.fire_pct,
    clearPct: settings.clear_pct,
    remainingMinutes: 0,
    clearWindowMinutes: settings.clear_window_minutes,
    requests: state.window_requests,
    minRequests: settings.min_window_requests,
    owedCount: state.owed_count,
  };
  if (!settings.enabled || !state.alerts_enabled) {
    return { ...base, kind: 'disabled' };
  }
  if (state.archive_stale) {
    return { ...base, kind: 'archive_stale' };
  }
  if (state.quiet_hours_active) {
    return { ...base, kind: 'quiet_hours' };
  }
  if (state.decision === 'fire') {
    return { ...base, kind: 'fire' };
  }
  if (state.decision === 'recover') {
    return { ...base, kind: 'recover' };
  }
  if (state.send_kind === 'elevated' && state.decision === 'hold') {
    return { ...base, kind: 'catch_up' };
  }
  if (state.cooldown_active && state.phase === 'clear') {
    return { ...base, kind: 'cooldown' };
  }
  if (state.phase === 'firing' && state.clear_below_since !== null) {
    return {
      ...base,
      kind: 'firing_remaining',
      remainingMinutes: remainingClearMinutes(
        state.clear_below_since,
        settings.clear_window_minutes,
        now,
      ),
    };
  }
  if (state.phase === 'firing') {
    return { ...base, kind: 'firing_no_stamp' };
  }
  if (state.window_pct === null) {
    return { ...base, kind: 'undersampled' };
  }
  return base;
}

const NEXT_STEP_KEY: Record<CodexCapacityNextStepKind, I18nKey> = {
  disabled: 'codex_capacity.state.next_step.disabled',
  archive_stale: 'codex_capacity.state.next_step.archive_stale',
  quiet_hours: 'codex_capacity.state.next_step.quiet_hours',
  cooldown: 'codex_capacity.state.next_step.cooldown',
  fire: 'codex_capacity.state.next_step.fire',
  recover: 'codex_capacity.state.next_step.recover',
  catch_up: 'codex_capacity.state.next_step.catch_up',
  firing_remaining: 'codex_capacity.state.next_step.firing_remaining',
  firing_no_stamp: 'codex_capacity.state.next_step.firing_no_stamp',
  undersampled: 'codex_capacity.state.next_step.undersampled',
  hold: 'codex_capacity.state.next_step.hold',
};

export function nextStepKey(kind: CodexCapacityNextStepKind): I18nKey {
  return NEXT_STEP_KEY[kind];
}

export function nextStepParams(step: CodexCapacityNextStep): Record<string, string> {
  const pct =
    step.kind === 'fire' || step.kind === 'hold' ? step.firePct : step.clearPct;
  const minutes = step.kind === 'firing_no_stamp' ? step.clearWindowMinutes : step.remainingMinutes;
  return {
    pct: Number.isFinite(pct) ? pct.toFixed(1) : '—',
    minutes: String(minutes),
    count: String(step.kind === 'catch_up' ? step.owedCount : step.requests),
    min: String(step.minRequests),
  };
}

export function remainingClearMinutes(
  clearBelowSince: string,
  clearWindowMinutes: number,
  now: Date,
): number {
  const sinceMs = Date.parse(clearBelowSince);
  if (!Number.isFinite(sinceMs)) return clearWindowMinutes;
  const elapsedMinutes = (now.getTime() - sinceMs) / 60_000;
  return Math.max(0, Math.ceil(clearWindowMinutes - elapsedMinutes));
}

export interface SettingsValidationIssue {
  readonly field: string;
  readonly key: I18nKey;
  readonly params: Record<string, string>;
}

const SETTINGS_ISSUE_KEY: Record<CodexCapacitySettingsIssueCode, I18nKey> = {
  fire_pct: 'codex_capacity.settings.error.fire_pct',
  clear_pct_min: 'codex_capacity.settings.error.clear_pct_min',
  clear_pct_vs_fire: 'codex_capacity.settings.error.clear_pct_vs_fire',
  window_minutes: 'codex_capacity.settings.error.window_minutes',
  clear_window_minutes: 'codex_capacity.settings.error.clear_window_minutes',
  retention_hours: 'codex_capacity.settings.error.retention_hours',
  rollup_lookback_minutes: 'codex_capacity.settings.error.rollup_lookback_minutes',
  min_window_requests: 'codex_capacity.settings.error.min_window_requests',
  audience_days: 'codex_capacity.settings.error.audience_days',
  max_sends_per_pass: 'codex_capacity.settings.error.max_sends_per_pass',
  cooldown_hours: 'codex_capacity.settings.error.cooldown_hours',
  quiet_hours_pair: 'codex_capacity.settings.error.quiet_hours_pair',
  quiet_hours_range: 'codex_capacity.settings.error.quiet_hours_range',
  quiet_hours_tz: 'codex_capacity.settings.error.quiet_hours_tz',
  window_exceeds_retention: 'codex_capacity.settings.error.window_exceeds_retention',
  clear_window_exceeds_retention: 'codex_capacity.settings.error.clear_window_exceeds_retention',
};

/**
 * Same function as platform PATCH (`validateCodexCapacityAlertSettings`).
 * The form refuses to send when this returns an issue, so a 400 is not
 * how the operator first hears the allowed window.
 */
export function validateCodexCapacitySettings(
  merged: CodexCapacityAlertSettings,
): SettingsValidationIssue | null {
  const issue = validateCodexCapacityAlertSettings(merged);
  if (issue === null) return null;
  return {
    field: issue.field,
    key: SETTINGS_ISSUE_KEY[issue.code],
    params: issue.params,
  };
}

export interface CodexCapacitySettingsDraft {
  readonly fire_pct: string;
  readonly clear_pct: string;
  readonly window_minutes: string;
  readonly clear_window_minutes: string;
  readonly min_window_requests: string;
  readonly audience_days: string;
  readonly max_sends_per_pass: string;
  readonly cooldown_hours: string;
  readonly quiet_hours_start_hour: string;
  readonly quiet_hours_end_hour: string;
  readonly quiet_hours_tz: string;
  readonly retention_hours: string;
  readonly rollup_lookback_minutes: string;
  readonly reason: string;
}

export function settingsToDraft(settings: CodexCapacityAlertSettings): CodexCapacitySettingsDraft {
  return {
    fire_pct: String(settings.fire_pct),
    clear_pct: String(settings.clear_pct),
    window_minutes: String(settings.window_minutes),
    clear_window_minutes: String(settings.clear_window_minutes),
    min_window_requests: String(settings.min_window_requests),
    audience_days: String(settings.audience_days),
    max_sends_per_pass: String(settings.max_sends_per_pass),
    cooldown_hours: String(settings.cooldown_hours),
    quiet_hours_start_hour:
      settings.quiet_hours_start_hour === null ? '' : String(settings.quiet_hours_start_hour),
    quiet_hours_end_hour:
      settings.quiet_hours_end_hour === null ? '' : String(settings.quiet_hours_end_hour),
    quiet_hours_tz: settings.quiet_hours_tz,
    retention_hours: String(settings.retention_hours),
    rollup_lookback_minutes: String(settings.rollup_lookback_minutes),
    reason: settings.reason ?? '',
  };
}

export type ParseDraftResult =
  | { readonly ok: true; readonly body: UpdateCodexCapacityAlertSettingsRequest; readonly merged: CodexCapacityAlertSettings }
  | { readonly ok: false; readonly issue: SettingsValidationIssue };

function parseNumberField(
  raw: string,
  field: string,
  integer: boolean,
): { ok: true; value: number } | { ok: false; issue: SettingsValidationIssue } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return {
      ok: false,
      issue: { field, key: 'codex_capacity.settings.error.required', params: {} },
    };
  }
  const value = Number(trimmed);
  if (!Number.isFinite(value)) {
    return {
      ok: false,
      issue: { field, key: 'codex_capacity.settings.error.number', params: {} },
    };
  }
  if (integer && !Number.isInteger(value)) {
    return {
      ok: false,
      issue: { field, key: 'codex_capacity.settings.error.integer', params: {} },
    };
  }
  return { ok: true, value };
}

function parseNullableHour(
  raw: string,
  field: string,
): { ok: true; value: number | null } | { ok: false; issue: SettingsValidationIssue } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: true, value: null };
  return parseNumberField(trimmed, field, true);
}

export function parseSettingsDraft(
  draft: CodexCapacitySettingsDraft,
  current: CodexCapacityAlertSettings,
): ParseDraftResult {
  const fire = parseNumberField(draft.fire_pct, 'fire_pct', false);
  if (!fire.ok) return fire;
  const clear = parseNumberField(draft.clear_pct, 'clear_pct', false);
  if (!clear.ok) return clear;
  const windowMinutes = parseNumberField(draft.window_minutes, 'window_minutes', true);
  if (!windowMinutes.ok) return windowMinutes;
  const clearWindow = parseNumberField(draft.clear_window_minutes, 'clear_window_minutes', true);
  if (!clearWindow.ok) return clearWindow;
  const minRequests = parseNumberField(draft.min_window_requests, 'min_window_requests', true);
  if (!minRequests.ok) return minRequests;
  const audienceDays = parseNumberField(draft.audience_days, 'audience_days', true);
  if (!audienceDays.ok) return audienceDays;
  const maxSends = parseNumberField(draft.max_sends_per_pass, 'max_sends_per_pass', true);
  if (!maxSends.ok) return maxSends;
  const cooldown = parseNumberField(draft.cooldown_hours, 'cooldown_hours', true);
  if (!cooldown.ok) return cooldown;
  const quietStart = parseNullableHour(draft.quiet_hours_start_hour, 'quiet_hours_start_hour');
  if (!quietStart.ok) return quietStart;
  const quietEnd = parseNullableHour(draft.quiet_hours_end_hour, 'quiet_hours_end_hour');
  if (!quietEnd.ok) return quietEnd;
  const retention = parseNumberField(draft.retention_hours, 'retention_hours', true);
  if (!retention.ok) return retention;
  const rollup = parseNumberField(draft.rollup_lookback_minutes, 'rollup_lookback_minutes', true);
  if (!rollup.ok) return rollup;

  const reason = draft.reason.trim();
  const merged: CodexCapacityAlertSettings = {
    ...current,
    fire_pct: fire.value,
    clear_pct: clear.value,
    window_minutes: windowMinutes.value,
    clear_window_minutes: clearWindow.value,
    min_window_requests: minRequests.value,
    audience_days: audienceDays.value,
    max_sends_per_pass: maxSends.value,
    cooldown_hours: cooldown.value,
    quiet_hours_start_hour: quietStart.value,
    quiet_hours_end_hour: quietEnd.value,
    quiet_hours_tz: draft.quiet_hours_tz,
    retention_hours: retention.value,
    rollup_lookback_minutes: rollup.value,
    reason: reason.length > 0 ? reason : null,
  };
  const invalid = validateCodexCapacitySettings(merged);
  if (invalid !== null) return { ok: false, issue: invalid };
  return {
    ok: true,
    merged,
    body: {
      fire_pct: merged.fire_pct,
      clear_pct: merged.clear_pct,
      window_minutes: merged.window_minutes,
      clear_window_minutes: merged.clear_window_minutes,
      min_window_requests: merged.min_window_requests,
      audience_days: merged.audience_days,
      max_sends_per_pass: merged.max_sends_per_pass,
      cooldown_hours: merged.cooldown_hours,
      quiet_hours_start_hour: merged.quiet_hours_start_hour,
      quiet_hours_end_hour: merged.quiet_hours_end_hour,
      quiet_hours_tz: merged.quiet_hours_tz,
      retention_hours: merged.retention_hours,
      rollup_lookback_minutes: merged.rollup_lookback_minutes,
      reason: merged.reason,
    },
  };
}

export function formatIncidentDurationMinutes(
  startedAt: string,
  recoveredAt: string | null,
  now: Date,
): number | null {
  const startMs = Date.parse(startedAt);
  if (!Number.isFinite(startMs)) return null;
  const endMs = recoveredAt === null ? now.getTime() : Date.parse(recoveredAt);
  if (!Number.isFinite(endMs)) return null;
  return Math.max(0, Math.round((endMs - startMs) / 60_000));
}

export function parseAccountIdList(raw: string): readonly string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const piece of raw.split(/[\s,]+/)) {
    const id = piece.trim();
    if (id.length === 0 || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}
